import { AbilityBuilder, createMongoAbility } from '@casl/ability';
import { useMeOptions, type UserEntry } from '../api';
import { useQuery } from '@tanstack/react-query';
import { subject } from '@casl/ability';
import { selectedProjectStore } from 'features/projects';
import type { AppAbility } from '../types';
import { Subjects, EmergencyAbilities, RuleAbilities, MetricAbilities } from '../types';
function defineAbilityFor(user: UserEntry | undefined) {
  const { can, build, cannot } = new AbilityBuilder<AppAbility>(createMongoAbility);

  const userProjects = user?.projects.map((p) => p.id) || [];

  if (user?.role === 'admin') {
    can('manage', Subjects.RULES);
    can('manage', Subjects.METRICS);
    can('manage', Subjects.USERS);
    can(EmergencyAbilities.TOGGLE, Subjects.EMERGENCY);
  }

  if (user?.role === 'supervisor') {
    can('manage', Subjects.METRICS);
    can('manage', Subjects.RULES);
    can(EmergencyAbilities.TOGGLE, Subjects.EMERGENCY);
  }

  if (user?.role === 'user') {
    can(MetricAbilities.READ, Subjects.METRICS);
    can(
      [MetricAbilities.UPDATE, MetricAbilities.CREATE, MetricAbilities.DELETE, MetricAbilities.REMOVE_FROM_GROUP],
      Subjects.METRICS,
      {
        project_id: { $in: userProjects },
      }
    );
    can(RuleAbilities.READ, Subjects.RULES);
    can(RuleAbilities.DUPLICATE, Subjects.RULES);
    can([RuleAbilities.UPDATE, RuleAbilities.CHANGE_STATUS], Subjects.RULES, {
      project_id: { $in: userProjects },
    });
  }

  if (!user?.role) {
    cannot('manage', Subjects.METRICS);
    cannot('manage', Subjects.USERS);
    cannot('manage', Subjects.RULES);
    cannot('manage', Subjects.EMERGENCY);
  }

  return build();
}

const useAccess = () => {
  const { data: me } = useQuery(useMeOptions);

  const abilities = defineAbilityFor(me);

  const checkMetricAbility = (
    ability: MetricAbilities | 'manage',
    metricProjectId = selectedProjectStore.selectedProject?.id
  ): boolean => {
    if (metricProjectId === null || metricProjectId === undefined) {
      return false;
    }

    return abilities.can(ability, subject(Subjects.METRICS, { project_id: Number(metricProjectId) }));
  };

  const checkRuleAbility = (
    ability: RuleAbilities | 'manage',
    ruleProjectId = selectedProjectStore.selectedProject?.id
  ): boolean => {
    if (ruleProjectId === null || ruleProjectId === undefined) {
      return false;
    }

    return abilities.can(ability, subject(Subjects.RULES, { project_id: Number(ruleProjectId) }));
  };

  return { abilities, checkMetricAbility, checkRuleAbility };
};

export default useAccess;
